<!--
  CustomerStatusesFilter renders a filter with dropdown options for customer
  statuses and reads/writes from the `customer_statuses` URL query param.
-->

<script>
import { iFilter } from '@inscribe/signal';
import { getAndSyncQueryParams, updateQueryParams } from '@/utils/queryParams';
import { InscribeEventsLogger, INSCRIBE_EVENTS } from '@/events-logger';

const PARAM_TO_LABEL_MAP = {
  approved: 'Approved',
  rejected: 'Rejected',
  unknown: 'No Status',
};

export default {
  components: {
    iFilter,
  },
  props: {
    entity: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: false,
      default: 'Customer Status',
    },
    analyticsLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      params: getAndSyncQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
      }),
      options: Object.values(PARAM_TO_LABEL_MAP).map((label) => ({ label })),
    };
  },
  computed: {
    queryParamName() {
      return this.entity === 'customers' ? 'status' : 'customer_statuses';
    },
    value() {
      const customer_statuses = this.params[this.queryParamName];
      if (!customer_statuses) {
        return [];
      }
      let paramsAsArray = [];
      if (typeof customer_statuses === 'string') {
        paramsAsArray = [customer_statuses];
      } else {
        paramsAsArray = customer_statuses;
      }
      // map query params like 'approved' to dropdown values like 'Approved'
      return paramsAsArray.map((param) => PARAM_TO_LABEL_MAP[param]);
    },
  },
  watch: {
    '$route.fullPath': {
      handler: function (newFullPath) {
        this.params = getAndSyncQueryParams({
          fullPath: newFullPath,
          router: this.$router,
          store: this.$store,
        });
      },
    },
  },
  methods: {
    onSelectionChanged(updated) {
      // this particular query param expects lowercase values like `approved`
      const mapEntries = Object.entries(PARAM_TO_LABEL_MAP);
      const updatedParamVals = updated.selection.map((item) => {
        const [param] = mapEntries.find(([_, label]) => label === item.label);
        return param;
      });
      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          [this.queryParamName]:
            updatedParamVals.length > 0 ? updatedParamVals : undefined,
        },
      });
      InscribeEventsLogger.logEvent(INSCRIBE_EVENTS.CUSTOMERS_SEARCH_CLICKED, {
        itemName: `filter_status`,
      });
    },
  },
};
</script>

<template>
  <iFilter
    :label="label"
    data-testid="customer-status-filter"
    :data-dd-action-name="analyticsLabel"
    :options="options"
    :model-value="value"
    :searchable="false"
    @selection-changed="onSelectionChanged"
  />
</template>
